/**
 * D7 + D9 + D11 — Admin → Outbound Voice tab.
 *
 * Single landing surface for outbound voice configuration + status:
 *   - Setup checklist (D7): attested / spend cap set / trunk created / flag enabled
 *   - Tenant health summary card (D7): active calls vs cap, spend today vs cap, breaker state, last campaign
 *   - Settings form (D7): 10 MANAGED_SETTINGS_FIELDS
 *   - Active calls panel (D9): in-flight calls with auto-refresh
 *   - Quick-launch button (D11): single-recipient test call
 *   - Attestation modal entry (D3)
 *
 * Per the v3 design (§13 + §14): this tab is the production-grade
 * landing page for the whole outbound voice surface. Spend cap is
 * REQUIRED — the setup checklist guides the admin to set it before
 * the dispatcher will run.
 */
import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { COLORS } from "../../theme/colors";
import { Button, Badge, Card, Input, Modal } from "../../components/ui";
import { outboundVoiceApi, agentApi, campaignApi } from "../../api/client";
import OutboundVoiceAttestationModal from "./OutboundVoiceAttestationModal";

const MANAGED_FIELDS = [
  { key: "voice_outbound_daily_spend_cap_usd", label: "Daily spend cap (USD)", type: "number", required: true, helper: "REQUIRED. Dispatcher refuses to run without this." },
  { key: "voice_outbound_enabled", label: "Outbound voice enabled", type: "boolean", required: false, helper: "Master switch. ON → campaigns dispatch. OFF → all in-flight drain, new dispatch refused." },
  { key: "voice_outbound_max_concurrent_calls", label: "Max concurrent calls (tenant)", type: "number", required: false, default: 10 },
  { key: "voice_outbound_max_cps", label: "Max CPS (calls per second)", type: "number", required: false, default: 1.0 },
  { key: "voice_outbound_max_calls_per_day", label: "Max calls per day", type: "number", required: false, default: 1000 },
  { key: "voice_outbound_default_amd_strategy", label: "Default AMD strategy", type: "enum", options: ["off", "agent", "hybrid", "carrier"], default: "hybrid" },
  { key: "voice_outbound_default_ring_timeout_s", label: "Default ring timeout (s)", type: "number", default: 30 },
  { key: "voice_outbound_default_max_call_duration_s", label: "Default max call duration (s)", type: "number", default: 600 },
  { key: "voice_outbound_default_voicemail_action", label: "Default voicemail action", type: "enum", options: ["hangup", "leave_message"], default: "hangup" },
  { key: "voice_outbound_test_phone_number", label: "Test phone number (E.164)", type: "string", helper: "Tenant-owned number used for wet-trunk tests." },
];

// Exported for unit testing — drop null/undefined entries so the backend
// treats omitted keys as "no change" (never clobber a set value with null
// when the admin only edited a subset of the form).
export function buildSettingsPatch(settings) {
  const patch = {};
  for (const [k, v] of Object.entries(settings || {})) {
    if (v !== null && v !== undefined) patch[k] = v;
  }
  return patch;
}

// Exported for unit testing — the D7 setup-checklist boolean flags.
export function computeSetupFlags({ attestation, settings = {}, trunkCount = 0 }) {
  return {
    attested: !!attestation?.attested,
    spendCap: settings.voice_outbound_daily_spend_cap_usd != null,
    trunkCreated: trunkCount > 0,
    flagEnabled: !!settings.voice_outbound_enabled,
  };
}

// Exported for unit testing — E.164 shape used by the quick-launch guard.
export function isValidE164(phone) {
  return /^\+\d{8,15}$/.test(phone);
}

export default function OutboundVoiceTab() {
  const [attestation, setAttestation] = useState(null);
  const [settings, setSettings] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);
  const [activeCalls, setActiveCalls] = useState([]);
  const [trunkCount, setTrunkCount] = useState(0);  // D7: live trunk-created flag
  const [showAttestModal, setShowAttestModal] = useState(false);
  const [showQuickLaunch, setShowQuickLaunch] = useState(false);

  // Initial load.
  useEffect(() => {
    refreshAttestation();
    refreshSettings();
    refreshActiveCalls();
    refreshTrunks();
  }, []);

  // Active calls auto-refresh every 3s while tab visible.
  useEffect(() => {
    const interval = setInterval(refreshActiveCalls, 3000);
    return () => clearInterval(interval);
  }, []);

  const refreshAttestation = async () => {
    try {
      const s = await outboundVoiceApi.attestationStatus();
      setAttestation(s);
    } catch (e) { console.error("attestation load failed", e); }
  };

  const refreshSettings = async () => {
    try {
      // D7 scoped route — returns the 10 voice-outbound managed
      // fields (null when unset) so the form renders deterministically.
      const data = await outboundVoiceApi.getSettings();
      const flat = {};
      for (const f of MANAGED_FIELDS) {
        flat[f.key] = data?.[f.key] ?? null;
      }
      setSettings(flat);
    } catch (e) { console.error("settings load failed", e); }
  };

  const refreshActiveCalls = useCallback(async () => {
    try {
      const res = await outboundVoiceApi.activeCalls(100);
      setActiveCalls(res.data || []);
    } catch (e) { /* silent on transient failures — panel renders stale */ }
  }, []);

  // D7 — live "voice trunk created" flag (was hardcoded false).
  // outboundVoiceApi.listTrunks() returns a list of trunk rows; any
  // active (non-soft-deleted) row satisfies the setup checklist item.
  const refreshTrunks = useCallback(async () => {
    try {
      const t = await outboundVoiceApi.listTrunks();
      const rows = t?.data || t || [];
      setTrunkCount(Array.isArray(rows) ? rows.length : 0);
    } catch (e) { /* silent — keep checklist conservative on failure */ }
  }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      // Drop null entries — backend treats omitted keys as "no
      // change" so we don't clobber a previously-set value with null
      // when the user only edited a subset of the form.
      const patch = buildSettingsPatch(settings);
      await outboundVoiceApi.updateSettings(patch);
      await refreshSettings();
    } catch (e) {
      alert(`Save failed: ${e.message}`);
    } finally {
      setSavingSettings(false);
    }
  };

  // D7 — trunkCreated comes from the live trunk count via
  // outboundVoiceApi.listTrunks() (was hardcoded false pre-fix, which made
  // the checklist always look incomplete even after the operator had
  // provisioned a trunk in the sibling tab).
  const flags = computeSetupFlags({ attestation, settings, trunkCount });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* G-LINK — deep link back to the unified Voice Console. */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <a
          href="#admin-voice-console"
          onClick={(e) => {
            e.preventDefault();
            window.dispatchEvent(new CustomEvent("admin:goto-tab", { detail: { id: "voice_console" } }));
          }}
          style={{ fontSize: 12, color: COLORS.accent || "#5960b2", textDecoration: "none" }}
        >
          ← Voice Console
        </a>
      </div>
      <SetupChecklist flags={flags} onAttestClick={() => setShowAttestModal(true)} />
      <HealthSummary activeCount={activeCalls.length} settings={settings} attestation={attestation} />

      <ActiveCallsPanel calls={activeCalls} />

      <div style={{ display: "flex", gap: 12 }}>
        <Button onClick={() => setShowAttestModal(true)} variant={attestation?.attested ? "ghost" : "primary"}>
          {attestation?.attested ? "Review attestation" : "Attest to enable outbound"}
        </Button>
        <Button onClick={() => setShowQuickLaunch(true)} variant="ghost">
          Place test call
        </Button>
      </div>

      <SettingsForm
        settings={settings}
        onChange={setSettings}
        onSave={saveSettings}
        saving={savingSettings}
      />

      <OutboundVoiceAttestationModal
        open={showAttestModal}
        onClose={() => setShowAttestModal(false)}
        onAttested={() => { refreshAttestation(); setShowAttestModal(false); }}
      />
      <QuickLaunchModal open={showQuickLaunch} onClose={() => setShowQuickLaunch(false)} />
    </div>
  );
}

// ─── Setup checklist (D7) ────────────────────────────────


function SetupChecklist({ flags, onAttestClick }) {
  const items = [
    { key: "attested", label: "Attestation signed", done: flags.attested, cta: !flags.attested && (
      <Button onClick={onAttestClick} variant="link" style={{ padding: 0, fontSize: 13 }}>Attest now →</Button>
    )},
    { key: "spendCap", label: "Daily spend cap configured", done: flags.spendCap, cta: !flags.spendCap && (
      <span style={{ fontSize: 13, color: COLORS.textMuted }}>Set below in Settings →</span>
    )},
    { key: "trunkCreated", label: "Voice trunk created", done: flags.trunkCreated, cta: !flags.trunkCreated && (
      <span style={{ fontSize: 13, color: COLORS.textMuted }}>Go to Voice Trunks tab →</span>
    )},
    { key: "flagEnabled", label: "Outbound voice flag enabled", done: flags.flagEnabled, cta: !flags.flagEnabled && (
      <span style={{ fontSize: 13, color: COLORS.textMuted }}>Toggle below in Settings →</span>
    )},
  ];
  const allDone = items.every(i => i.done);
  return (
    <Card padding="16px 20px">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>Setup checklist</div>
        {allDone && <Badge color={COLORS.green}>Ready to dispatch</Badge>}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map(item => (
          <div key={item.key} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
            <span style={{ width: 18, color: item.done ? COLORS.green : COLORS.textMuted, fontWeight: 700 }}>
              {item.done ? "✓" : "○"}
            </span>
            <span style={{ flex: 1, color: item.done ? COLORS.textMuted : COLORS.text }}>{item.label}</span>
            {item.cta}
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─── Health summary (D7) ─────────────────────────────────


function HealthSummary({ activeCount, settings, attestation }) {
  const maxConcurrent = settings.voice_outbound_max_concurrent_calls || 10;
  const dailyCap = settings.voice_outbound_daily_spend_cap_usd;
  return (
    <Card padding="16px 20px">
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Health summary</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 }}>
        <Metric label="Active calls" value={`${activeCount} / ${maxConcurrent}`} color={activeCount > maxConcurrent * 0.8 ? COLORS.orange : COLORS.green} />
        <Metric label="Daily spend cap" value={dailyCap != null ? `$${Number(dailyCap).toFixed(2)}` : "(not set)"} color={dailyCap == null ? COLORS.pink : COLORS.textMuted} />
        <Metric label="Attestation" value={attestation?.attested ? "Active" : "Required"} color={attestation?.attested ? COLORS.green : COLORS.pink} />
        <Metric label="Outbound enabled" value={settings.voice_outbound_enabled ? "Yes" : "No"} color={settings.voice_outbound_enabled ? COLORS.green : COLORS.textMuted} />
      </div>
    </Card>
  );
}

function Metric({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: COLORS.textMuted }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: color || COLORS.text, marginTop: 4 }}>{value}</div>
    </div>
  );
}

// ─── Active calls panel (D9) ─────────────────────────────


function ActiveCallsPanel({ calls }) {
  return (
    <Card padding="16px 20px">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>Active calls</div>
        <Badge color={COLORS.cyan}>{calls.length}</Badge>
      </div>
      {calls.length === 0 ? (
        <div style={{ fontSize: 13, color: COLORS.textMuted, padding: "20px 0", textAlign: "center" }}>
          No active outbound calls right now.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {calls.map(c => (
            <ActiveCallRow key={c.message_id} call={c} />
          ))}
        </div>
      )}
    </Card>
  );
}

function ActiveCallRow({ call }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr 1fr 80px",
        gap: 12,
        padding: "8px 12px",
        background: COLORS.surface,
        borderRadius: 6,
        fontSize: 13,
        alignItems: "center",
      }}
    >
      <span style={{ fontFamily: "monospace" }}>{call.recipient_phone_masked}</span>
      <span>{call.campaign_name || "—"}</span>
      <span style={{ color: COLORS.textMuted }}>Attempt {call.attempt_number}</span>
      <span>{call.amd_verdict ? <Badge color={call.amd_verdict === "human" ? COLORS.green : call.amd_verdict === "voicemail" ? COLORS.cyan : COLORS.textMuted}>{call.amd_verdict}</Badge> : "—"}</span>
      <span style={{ color: COLORS.textMuted, fontSize: 11 }}>{call.dispatched_at?.split("T")[1]?.slice(0, 8) || ""}</span>
    </div>
  );
}

// ─── Settings form (D7) ──────────────────────────────────


function SettingsForm({ settings, onChange, onSave, saving }) {
  return (
    <Card padding="20px 22px">
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 16 }}>Outbound voice settings</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {MANAGED_FIELDS.map(f => (
          <SettingField key={f.key} field={f} value={settings[f.key]} onChange={(v) => onChange({ ...settings, [f.key]: v })} />
        ))}
      </div>
      <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end" }}>
        <Button onClick={onSave} disabled={saving}>{saving ? "Saving…" : "Save settings"}</Button>
      </div>
    </Card>
  );
}

function SettingField({ field, value, onChange }) {
  const required = field.required ? <span style={{ color: COLORS.pink, marginLeft: 4 }}>*</span> : null;
  if (field.type === "boolean") {
    return (
      <label style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
        <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />
        <span>{field.label}{required}</span>
        {field.helper && <span style={{ color: COLORS.textMuted, fontSize: 12 }}>— {field.helper}</span>}
      </label>
    );
  }
  if (field.type === "enum") {
    return (
      <div>
        <div style={{ fontSize: 13, marginBottom: 4 }}>{field.label}{required}</div>
        <select
          value={value ?? field.default ?? ""}
          onChange={e => onChange(e.target.value)}
          style={{
            padding: "8px 10px",
            background: COLORS.bg,
            color: COLORS.text,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 6,
            minWidth: 200,
          }}
        >
          {field.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {field.helper && <div style={{ color: COLORS.textMuted, fontSize: 12, marginTop: 4 }}>{field.helper}</div>}
      </div>
    );
  }
  return (
    <Input
      label={<>{field.label}{required}</>}
      type={field.type === "number" ? "number" : "text"}
      value={value ?? ""}
      onChange={e => onChange(field.type === "number" ? (e.target.value ? Number(e.target.value) : null) : e.target.value)}
      helper={field.helper}
      style={{ maxWidth: 320 }}
    />
  );
}

// ─── Quick-launch modal (D11) ────────────────────────────


function QuickLaunchModal({ open, onClose }) {
  const [agents, setAgents] = useState([]);
  const [trunks, setTrunks] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [trunkId, setTrunkId] = useState("");
  const [phone, setPhone] = useState("");
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [a, t] = await Promise.all([agentApi.list(), outboundVoiceApi.listTrunks()]);
        setAgents(a?.data || a || []);
        setTrunks(t?.data || t || []);
      } catch (e) { setError(e.message); }
    })();
  }, [open]);

  const launch = async () => {
    if (!agentId || !trunkId || !phone) {
      setError("All fields required");
      return;
    }
    if (!isValidE164(phone)) {
      setError("Phone must be E.164 (e.g. +14155551234)");
      return;
    }
    setLaunching(true);
    setError(null);
    try {
      // D2 — payload shape matches the B5-merge CampaignCreate
      // contract: voice fields land at the API top level, not nested
      // under `config.voice`. Pre-fix the nested shape made the
      // backend silently default voice_trunk_id to None → dispatcher
      // rejected with "no trunk".
      // D5 — campaignApi.create / .trigger route through the
      // auth-refresh client. Pre-fix the raw fetch() with
      // localStorage Bearer token bypassed 401 auto-refresh, so a
      // demo run after sitting on the tab for >15min failed silently
      // with HTTP 401.
      // Recipients shape: list[string] (the backend parses both
      // E.164 strings and {phone_e164: ...} dicts; strings are the
      // tighter contract).
      const created = await campaignApi.create({
        name: `Quick Launch ${new Date().toISOString()}`,
        channel: "voice",
        agent_id: agentId,
        use_agent: true,
        recipients: [phone],
        rate_limit: 1,
        batch_size: 1,
        voice_trunk_id: trunkId,
        voice_recording_consent_disclosed: true,
        config: { quick_launch: true },
      });
      const campId = created?.data?.id || created?.id;
      if (!campId) throw new Error("No campaign id returned");
      await campaignApi.trigger(campId);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setLaunching(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Place test call (quick launch)"
      width={500}
      footer={
        <>
          <Button onClick={onClose} variant="ghost">Cancel</Button>
          <Button onClick={launch} disabled={launching}>{launching ? "Dispatching…" : "Place call"}</Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <div style={{ fontSize: 13, marginBottom: 4 }}>Agent</div>
          <select value={agentId} onChange={e => setAgentId(e.target.value)} style={{ width: "100%", padding: 8, background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}`, borderRadius: 6 }}>
            <option value="">— pick agent —</option>
            {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 13, marginBottom: 4 }}>Trunk</div>
          <select value={trunkId} onChange={e => setTrunkId(e.target.value)} style={{ width: "100%", padding: 8, background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}`, borderRadius: 6 }}>
            <option value="">— pick trunk —</option>
            {trunks.map(t => <option key={t.id} value={t.id}>{t.name} ({t.caller_id_e164})</option>)}
          </select>
        </div>
        <Input
          label="Recipient phone (E.164)"
          placeholder="+14155551234"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          helper="Must be E.164 format. Bypasses full campaign creation — for SE demos + ops smoke testing."
        />
        {error && <div style={{ color: COLORS.pink, fontSize: 13 }}>{error}</div>}
      </div>
    </Modal>
  );
}
